import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { UserProfileService } from 'src/app/nutrigenic/services/profile/user-profile.service';
import { PlanService } from 'src/app/nutrigenic/services/plan/plan.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Notify } from 'notiflix/build/notiflix-notify-aio';
import { Address } from 'src/app/nutrigenic/models/address-model';

@Component({
    selector: 'app-my-order-details',
    templateUrl: './my-order-details.component.html',
    styleUrls: ['./my-order-details.component.scss']
})
export class MyOrderDetailsComponent implements OnInit {

    @Input() orderId: number;
    @Output() back = new EventEmitter<void>();

    order: any = null;
    order_details: any = [];
    addons: any = [];
    loading: boolean = true;
    enableEditAddress: boolean = false;
    showRemoveAddonsConfirm: boolean = false;
    removeAddonsOrderId: number;
    removeAddonsWeek: number;

    constructor(
        private router: Router,
        private userProfileService: UserProfileService,
        private planService: PlanService,
        private spinner: NgxSpinnerService
    ) {}

    ngOnInit() {
        if (this.orderId) {
            this.loadOrderDetails(this.orderId);
        }
    }

    loadOrderDetails(id: number) {
        this.loading = true;
        this.spinner.show('orderDetailsSpinner');

        this.userProfileService.getUserOrder(this.orderId).subscribe({
            next: (response: any) => {
                const orderData = response.body.order;
                if (!orderData) {
                    this.loading = false;
                    this.spinner.hide('orderDetailsSpinner');
                    this.goBack();
                    return;
                }
                this.order = orderData;
                this.order.order_date = this.order.order_date ? formatDate(this.order.order_date, 'dd/MM/yyyy', 'en-GB') : null;
                this.order.pause_from = this.order.pause_from ? formatDate(this.order.pause_from, 'dd/MM/yyyy', 'en-GB') : null;
                this.order.pause_until = this.order.pause_until ? formatDate(this.order.pause_until, 'dd/MM/yyyy', 'en-GB') : null;
                this.order.cancel_from = this.order.cancel_from ? formatDate(this.order.cancel_from, 'dd/MM/yyyy', 'en-GB') : null;

                const orderDetails = this.order.order_details || this.order.order_detail;

                if (this.order.status === 1 || this.order.status === 8) {
                    this.loading = false;
                    this.spinner.hide('orderDetailsSpinner');
                    this.goBack();
                    return;
                }

                this.userProfileService.getOrderSummaryPriceBreakdown(id).subscribe((priceResponse) => {
                    this.order_details = orderDetails.filter((x: any) => !x.supplement_id && x.type !== 'addon' && x.meal?.type !== 'addon');
                    this.addons = orderDetails.filter((x: any) => x.supplement_id || x.type === 'addon' || x.meal?.type === 'addon');

                    this.order_details = Object.values(this.order_details.reduce((acc: any, cur: any) => {
                        acc[cur.week] = acc[cur.week] || { week: cur.week, details: [] };
                        acc[cur.week].details.push(cur);
                        return acc;
                    }, {}));

                    const now = new Date();
                    now.setHours(0, 0, 0, 0);
                    const currentAndUpcoming = this.order_details.filter((item: any) => {
                        const lastDelivery = new Date(Math.max(...item.details.map((d: any) => new Date(d.delivery_date).getTime())));
                        return lastDelivery >= now;
                    });
                    this.order_details = currentAndUpcoming.slice(0, 2);

                    this.order.weekly_prices = priceResponse.body.weekly_prices;
                    this.loading = false;
                    this.spinner.hide('orderDetailsSpinner');
                });
            },
            error: () => {
                this.loading = false;
                this.spinner.hide('orderDetailsSpinner');
                this.goBack();
            }
        });
    }

    goBack() {
        this.back.emit();
    }

    getOriginalWeekPrice(week: number) {
        return this.order?.weekly_prices?.[week - 1]?.["original-price"];
    }

    getDiscountedWeekPrice(week: number) {
        return this.order?.weekly_prices?.[week - 1]?.["discounted-price"];
    }

    getDiscountAppliedWeekPercentage(week: number) {
        return this.order?.weekly_prices?.[week - 1]?.["discount-percentage"];
    }

    hasDiscountForWeek(week: number) {
        return this.order?.weekly_prices?.[week - 1]?.["discount-percentage"] > 0;
    }

    groupByDeliveryDate(details: any[]): any[] {
        let deliveryGroup = details.reduce((acc: any, cur: any) => {
            acc[cur.delivery_date] = acc[cur.delivery_date] || { delivery_date: cur.delivery_date, orders: [] };
            acc[cur.delivery_date].orders.push(cur);
            return acc;
        }, {});
        return Object.values(deliveryGroup);
    }

    hasAddonsForWeek(item: any): boolean {
        return this.addons.some((x: any) => x.week === item.week);
    }

    getAddonsForDeliveryDate(week: number, deliveryDate: string): any[] {
        return this.addons.filter((x: any) => x.week === week && x.delivery_date === deliveryDate);
    }

    getAddonName(ord: any): string {
        return ord.meal?.name || ord.supplement?.name || '';
    }

    isSubscriptionValidState(order: any) {
        return (this.isSubscriptionActive(order) || order.status == 4 || order.status == 7);
    }

    isSubscriptionActive(order: any) {
        return order.status == 5;
    }

    editAddress() {
        this.enableEditAddress = true;
    }

    closeEditAddress() {
        this.enableEditAddress = false;
    }

    onAddressSelected(address: Address) {
        this.planService.changeDeliveryAddressForOrder(this.order.id, address).subscribe({
            next: () => {
                this.order.address = address.address;
                this.order.city = address.city;
                this.order.zip = address.zip;
                this.enableEditAddress = false;
                Notify.success('Address changed successfully', { position: 'right-top', timeout: 3000 });
            },
            error: (error) => {
                this.enableEditAddress = false;
                Notify.failure(error?.error?.message ?? 'Could not update address', { position: 'right-top', timeout: 3000 });
            }
        });
    }

    removeAddonsForWeek(orderId: number, week: number) {
        this.removeAddonsOrderId = orderId;
        this.removeAddonsWeek = week;
        this.showRemoveAddonsConfirm = true;
    }

    confirmRemoveAddons() {
        this.showRemoveAddonsConfirm = false;
        this.spinner.show('orderDetailsSpinner');
        const data = { order_id: this.removeAddonsOrderId, week: this.removeAddonsWeek, meals: [] };
        this.userProfileService.replaceOrderMealsFromWeek(data, 'addon').subscribe({
            next: () => {
                Notify.success('Add-ons removed successfully', { position: 'right-top', timeout: 3000 });
                this.loadOrderDetails(this.orderId);
            },
            error: (error) => {
                this.spinner.hide('orderDetailsSpinner');
                Notify.failure(error?.error?.message ?? 'Could not remove add-ons', { position: 'right-top', timeout: 3000 });
            }
        });
    }

    cancelRemoveAddons() {
        this.showRemoveAddonsConfirm = false;
    }

    openEditWeek(orderId: number, week: number, type: string = 'meals') {
        this.router.navigate(["/edit-week-plan"], { queryParams: { id: orderId, week: week, type: type } });
    }

    canEditWeek(order_details: any[], week: number) {
        if (week == 1) return false;
        const weekData = order_details.find((item: any) => item.week === week);
        if (!weekData?.details?.length) return false;
        const deliveryDate = new Date(weekData.details[0].delivery_date);
        deliveryDate.setDate(deliveryDate.getDate() - 2);
        const now = new Date();
        return now < deliveryDate;
    }

    formatDate(dateS: any) {
        let date = new Date(dateS);
        return (date.getDate().toString().length == 1 ? ("0" + date.getDate().toString()) : date.getDate().toString()) + "." + ((date.getMonth() + 1).toString().length == 1 ? ("0" + (date.getMonth() + 1).toString()) : (date.getMonth() + 1).toString()) + "." + date.getFullYear().toString();
    }
}
